<template>
    <div class="ui-page">
        <ui-page-header title="Auditoria de cobranças"
            :subtitle="track === 'legal'
                ? 'E-mails de solicitação e cobrança do Jurídico enviados à recuperanda, com prova de entrega e o conteúdo enviado.'
                : 'E-mails de solicitação e cobrança da fiscalização mensal enviados à recuperanda, com prova de entrega e o conteúdo enviado.'">
            <v-btn color="primary" depressed :disabled="!total" :loading="pdfLoading" @click="downloadPdf">
                <v-icon left small>mdi-file-pdf-box</v-icon>Gerar PDF da auditoria
            </v-btn>
        </ui-page-header>

        <!-- Auditoria separada por area. -->
        <div class="mb-4">
            <ui-pills :value="track" :items="trackItems" @input="changeTrack" />
        </div>

        <section class="rma-audit-stats mb-4" aria-label="Resumo das cobranças">
            <div class="ui-card rma-audit-stat">
                <span class="ui-label mb-0">E-mails de cobrança</span>
                <strong class="rma-audit-stat__value">{{ summary.total || 0 }}</strong>
            </div>
            <div class="ui-card rma-audit-stat">
                <span class="ui-label mb-0">Entregues</span>
                <strong class="rma-audit-stat__value" style="color: #15803d;">{{ summary.delivered || 0 }}</strong>
            </div>
            <div class="ui-card rma-audit-stat">
                <span class="ui-label mb-0">Aguardando confirmação</span>
                <strong class="rma-audit-stat__value">{{ summary.queued || 0 }}</strong>
            </div>
            <div class="ui-card rma-audit-stat">
                <span class="ui-label mb-0">Com falha</span>
                <strong class="rma-audit-stat__value" :style="summary.failed ? 'color: #b91c1c;' : ''">{{ summary.failed || 0 }}</strong>
            </div>
        </section>

        <ui-table-card clearable :can-clear="hasFilters" @clear="clearFilters">
            <template #toolbar>
                <v-autocomplete v-model="filters.client_id" :items="cases" item-value="id" item-text="name" placeholder="Todos os casos"
                    dense outlined hide-details clearable class="ui-toolbar__search" @change="applyFilters"></v-autocomplete>
                <v-text-field v-model="filters.from" type="date" label="De" dense outlined hide-details class="ui-toolbar__field" @change="applyFilters"></v-text-field>
                <v-text-field v-model="filters.to" type="date" label="Até" dense outlined hide-details class="ui-toolbar__field" @change="applyFilters"></v-text-field>
                <v-select v-model="filters.kind" :items="kindItems" placeholder="Todos os tipos" dense outlined hide-details clearable class="ui-toolbar__field"
                    :menu-props="{ offsetY: true }" @change="applyFilters"></v-select>
                <v-select v-model="filters.status" :items="statusItems" placeholder="Todas as situações" dense outlined hide-details clearable class="ui-toolbar__field"
                    :menu-props="{ offsetY: true }" @change="applyFilters"></v-select>
                <v-checkbox v-model="filters.include_copies" dense hide-details class="mt-0" label="Incluir cópias internas" @change="applyFilters"></v-checkbox>
            </template>

            <v-data-table :headers="headers" :items="rows" :loading="loading" disable-sort hide-default-footer
                no-data-text="Nenhuma cobrança encontrada" loading-text="Carregando...">
                <template v-slot:[`item.created_at`]="{ item }">
                    <span class="ui-nowrap">{{ formatDateTime(item.created_at) }}</span>
                </template>
                <template v-slot:[`item.client_name`]="{ item }">
                    <div class="ui-cell-main">{{ item.client_name }}</div>
                    <div class="ui-cell-sub">Competência {{ item.competence_label }}<span v-if="item.due_date"> · prazo {{ formatDate(item.due_date) }}</span></div>
                </template>
                <template v-slot:[`item.kind_label`]="{ item }">
                    <div>{{ item.kind_label.replace(/^(RMA|Jurídico) · /, "") }}</div>
                    <div class="ui-cell-sub text-truncate" style="max-width: 260px;" :title="item.subject">{{ item.subject }}</div>
                </template>
                <template v-slot:[`item.recipient`]="{ item }">
                    <div class="ui-cell-main">{{ item.recipient }}</div>
                    <div v-if="item.recipient_name" class="ui-cell-sub">{{ item.recipient_name }}</div>
                </template>
                <template v-slot:[`item.status`]="{ item }">
                    <ui-badge :text="item.status_label" :tone="statusTone(item.status)" />
                    <div v-if="item.delivered_at" class="ui-cell-sub">{{ formatDateTime(item.delivered_at) }}</div>
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                    <v-btn small text color="primary" :loading="receiptId === item.id" @click="openReceipt(item)">
                        <v-icon left small>mdi-file-pdf-box</v-icon>PDF
                    </v-btn>
                </template>
            </v-data-table>

            <template #footer>
                <ui-pagination :total="total" :page="page" :items-per-page="perPage" :disabled="loading"
                    @update:page="(value) => { page = value; load(); }" @update:itemsPerPage="(value) => { perPage = value; page = 1; load(); }" />
            </template>
        </ui-table-card>

        <p class="ui-small ui-muted mt-3">
            O PDF de cada e-mail traz destinatário, assunto, datas, a resposta do servidor de destino (Mailgun) e o conteúdo enviado.
            E-mails anteriores a 04/11/2026 não têm o conteúdo guardado; para eles constam os dados do envio e a prova de entrega.
        </p>
    </div>
</template>

<script>
import axios from "axios";
import { formatDate, formatDateTime, notifyError, trackFromRoute, TRACKS } from "@/components/rma/rma.util";

const KINDS = [
    { value: "rma_request", text: "Solicitação de documentos" },
    { value: "rma_reminder", text: "Lembrete" },
    { value: "rma_overdue", text: "Cobrança após o prazo" },
    { value: "rma_item_returned", text: "Documento devolvido" },
    { value: "rma_clarification", text: "Pedido de esclarecimento" },
    { value: "rma_pending_reinformed", text: "Pendências reinformadas" },
];

const STATUSES = [
    { value: "delivered", text: "Entregue" },
    { value: "queued", text: "Aguardando confirmação" },
    { value: "failed", text: "Falhou" },
    { value: "rejected", text: "Recusado" },
    { value: "error", text: "Não enviado" },
];

function emptyFilters() {
    return { client_id: null, from: "", to: "", kind: null, status: null, include_copies: false };
}

// Auditoria das cobrancas por area (Contabilidade / Juridico).
export default {
    name: "RmaAudit",

    data: () => ({
        track: "accounting",
        cases: [],
        rows: [],
        summary: {},
        total: 0,
        page: 1,
        perPage: 25,
        loading: false,
        pdfLoading: false,
        receiptId: null,
        filters: emptyFilters(),
        kindItems: KINDS,
        statusItems: STATUSES,
        headers: [
            { text: "Data", value: "created_at", width: 140 },
            { text: "Caso", value: "client_name" },
            { text: "Tipo", value: "kind_label" },
            { text: "Destinatário", value: "recipient" },
            { text: "Situação", value: "status", width: 170 },
            { text: "", value: "actions", align: "end", width: 90 },
        ],
    }),

    computed: {
        trackItems() {
            return TRACKS.map((item) => ({ value: item.value, text: item.text }));
        },
        hasFilters() {
            const f = this.filters;
            return !!(f.client_id || f.from || f.to || f.kind || f.status || f.include_copies);
        },
    },

    watch: {
        "$route.query.area"() {
            const track = trackFromRoute(this.$route);
            if (track === this.track) return;
            this.track = track;
            this.filters = emptyFilters();
            this.page = 1;
            this.loadCases();
            this.load();
        },
    },

    created() {
        this.track = trackFromRoute(this.$route);
        if (this.$route.query.caso) this.filters.client_id = Number(this.$route.query.caso);
        this.loadCases();
        this.load();
    },

    methods: {
        formatDate,
        formatDateTime,

        statusTone(status) {
            if (status === "delivered") return "success";
            if (status === "queued") return "neutral";
            return "danger";
        },

        params(extra = {}) {
            const f = this.filters;
            const params = { track: this.track, ...extra };
            if (f.client_id) params.client_id = f.client_id;
            if (f.from) params.from = f.from;
            if (f.to) params.to = f.to;
            if (f.kind) params.kind = f.kind;
            if (f.status) params.status = f.status;
            if (f.include_copies) params.include_copies = 1;
            return params;
        },

        changeTrack(track) {
            if (track === this.track) return;
            this.$router.replace({ query: { ...this.$route.query, area: track === "legal" ? "juridico" : undefined, caso: undefined } }).catch(() => {});
        },

        async loadCases() {
            try {
                const result = await axios.get("/api/admin/rma/audit/cases", { params: { track: this.track } });
                this.cases = result.data.content || [];
            } catch (error) {
                notifyError(this, error);
            }
        },

        async load() {
            this.loading = true;
            try {
                const result = await axios.get("/api/admin/rma/audit", { params: this.params({ page: this.page, perPage: this.perPage }) });
                const content = result.data.content || {};
                this.rows = content.data || [];
                this.summary = content.summary || {};
                this.total = (content.paging && content.paging.total) || 0;
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.loading = false;
            }
        },

        applyFilters() {
            this.page = 1;
            this.load();
        },

        clearFilters() {
            this.filters = emptyFilters();
            this.applyFilters();
        },

        openBlob(data) {
            const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
            window.open(url, "_blank");
            setTimeout(() => window.URL.revokeObjectURL(url), 60000);
        },

        // Erro do PDF vem como blob: converte para ler a mensagem da API.
        async notifyBlobError(error) {
            const data = error && error.response && error.response.data;
            if (data instanceof Blob) {
                try {
                    const parsed = JSON.parse(await data.text());
                    const message = parsed && parsed.content && parsed.content.error && parsed.content.error.message;
                    if (message) {
                        this.$dialog.notify.error(message, { position: "top-right", timeout: 6000 });
                        return;
                    }
                } catch (parseError) {
                    // segue para a mensagem padrao
                }
            }
            notifyError(this, error);
        },

        async openReceipt(item) {
            this.receiptId = item.id;
            try {
                const result = await axios.get(`/api/admin/rma/audit/${item.id}/receipt`, { responseType: "blob" });
                this.openBlob(result.data);
            } catch (error) {
                await this.notifyBlobError(error);
            } finally {
                this.receiptId = null;
            }
        },

        async downloadPdf() {
            this.pdfLoading = true;
            try {
                const result = await axios.get("/api/admin/rma/audit/pdf", { params: this.params(), responseType: "blob" });
                this.openBlob(result.data);
            } catch (error) {
                await this.notifyBlobError(error);
            } finally {
                this.pdfLoading = false;
            }
        },
    },
};
</script>

<style scoped>
.rma-audit-stats {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
}
.rma-audit-stat {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 14px 16px;
}
.rma-audit-stat__value {
    font-size: 22px;
    font-weight: 600;
}
@media (max-width: 900px) {
    .rma-audit-stats {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}
</style>
